import { getPreselected, isRequiredQuestion, isRequiredWhenSelected } from './question/helper/component-helper';
import { getAktorDetailsFields } from './question/types/aktor/aktor-detaljer';
import { answersToActor, isValidAktor } from './question/types/aktor/validation';
import { getDisponentDetaljerFields } from './question/types/disponent/disponent-detaljer';
import { answersToManager, isValidDisponent } from './question/types/disponent/validation';
import { answersToBeneficialOwner, getReelleRettighetshaverFields, isValid } from './question/types/rr/validation';
import { validerMonster } from './question/helper/validation-helper';
import { calculateTotalOwnershipShares } from './question/types/rr';
import {
    SPORSMAL_SYSTEM,
    SPORSMALSTYPE_AKTOR_CAP,
    SPORSMALSTYPE_AKTOR_LIST_CAP,
    SPORSMALSTYPE_AMOUNT_CAP,
    SPORSMALSTYPE_CHECKBOX_CAP,
    SPORSMALSTYPE_CRS_V2_CAP,
    SPORSMALSTYPE_DISPONENT_LIST_CAP,
    SPORSMALSTYPE_FATCA_V2_CAP,
    SPORSMALSTYPE_LAND_CAP,
    SPORSMALSTYPE_NUMBER_CAP,
    SPORSMALSTYPE_RADIO_CAP,
    SPORSMALSTYPE_RADIO_CLASSIC_CAP,
    SPORSMALSTYPE_RR_CAP,
    SPORSMALSTYPE_SELECT_CAP,
    SPORSMALSTYPE_TEXT_AREA_CAP,
    SPORSMALSTYPE_TEXT_CAP,
    BM_AKTOR_RR_NO_VALUE,
} from './constants/general';
import {
    BM_AKTOR_RR,
    BM_AKTOR_RR_DETALJER,
    BM_CRS,
    BM_MOTTA_UTLAND_V2,
    BM_OVERFORE_UTLAND_V2,
    PM_CRS,
    PM_MOTTA_UTLAND_V2,
    PM_OVERFORE_UTLAND_V2,
} from './constants/definisjoner';
import {
    getInternasjonaleOverforingerFields,
    isValidOverforingField,
} from './question/types/int-overforinger/validation';

export function validateThatAnswerHasTruthyValueAndNoError(answer) {
    return !!answer && !answer.error;
}

export function isSubQuestionAndNotTriggerExistsInAnswers(question, answers) {
    const requiredParentQuestion = answers[question.rootQuestion];
    if (question.isSubQuestion) {
        return (
            requiredParentQuestion !== undefined &&
            requiredParentQuestion.value &&
            !requiredParentQuestion.value.includes(question.requiredParent)
        );
    }
    return false;
}

function validateQuestionWithPossibleSubQuestion(question, answers) {
    const answer = answers[question.definisjon];

    if (question.isSubQuestion) {
        const requiredParentQuestion = answers[question.rootQuestion];
        if (answer === undefined) {
            return !(requiredParentQuestion && requiredParentQuestion.value.includes(question.requiredParent));
        }
        return !answer.error;
    }

    return validateThatAnswerHasTruthyValueAndNoError(answer);
}

function validateSibling(question, answers) {
    if (question.validateIfNotSet && question.validateIfNotSet.length > 0) {
        const siblingValue = answers[question.validateIfNotSet[0].definisjon];
        return siblingValue && !siblingValue.error;
    }
    return false;
}

export function validateCheckboxType(question, answers) {
    const answer = answers[question.definisjon];
    return validateThatAnswerHasTruthyValueAndNoError(answer);
}

function getCrsTriggerAnswer({ muligeSvar }) {
    return muligeSvar.filter((muligSvar) => muligSvar.undersporsmal && muligSvar.undersporsmal.length > 0)[0]
        .undersporsmal[0];
}

function questionTriggerCRS(question, answers) {
    const parentAnswer = answers.PM_CRS || answers.BM_CRS;
    // Type radio negative answer ends with '-2'
    if (parentAnswer && parentAnswer.value[0].endsWith('-2')) {
        return false;
    }
    if (question.definisjon !== PM_CRS && question.definisjon !== BM_CRS) {
        return false;
    }
    const crsTriggerAnswer = getCrsTriggerAnswer(question);
    return !!answers[crsTriggerAnswer.definisjon];
}

function getOverforingTriggerAnswer({ muligeSvar }) {
    return muligeSvar.filter((muligSvar) => muligSvar.undersporsmal && muligSvar.undersporsmal.length > 0)[0]
        .undersporsmal[0];
}

function questionTriggerOverforing(question, answers) {
    const isOverfore = [BM_OVERFORE_UTLAND_V2, PM_OVERFORE_UTLAND_V2].includes(question.definisjon);
    const parentAnswer = isOverfore
        ? answers.BM_OVERFORE_UTLAND_V2 || answers.PM_OVERFORE_UTLAND_V2
        : answers.BM_MOTTA_UTLAND_V2 || answers.PM_MOTTA_UTLAND_V2;

    // Type radio negative answer ends with '-2'
    if (parentAnswer && parentAnswer.value[0].endsWith('-2')) {
        return false;
    }
    if (
        ![BM_OVERFORE_UTLAND_V2, PM_OVERFORE_UTLAND_V2, BM_MOTTA_UTLAND_V2, PM_MOTTA_UTLAND_V2].includes(
            question.definisjon
        )
    ) {
        return false;
    }
    const overforingTriggerAnswer = getOverforingTriggerAnswer(question);
    return !!answers[overforingTriggerAnswer.definisjon];
}

function validateCRS(question, answers) {
    const crsTriggerAnswer = getCrsTriggerAnswer(question);
    const crsAnswer = answers[crsTriggerAnswer.definisjon];
    if (crsAnswer && crsAnswer.value) {
        return crsAnswer.value.filter((crsAnswer) => crsAnswer.error).length > 0;
    }
    return true;
}

function hasErrorInOverforing(question, answers) {
    const overforingTriggerAnswer = getOverforingTriggerAnswer(question);
    const overforingAnswer = answers[overforingTriggerAnswer.definisjon];
    const errors =
        overforingAnswer &&
        overforingAnswer.value &&
        overforingAnswer.value.reduce(
            (acc, cur) =>
                Object.keys(getInternasjonaleOverforingerFields()).filter(
                    (key) => !isValidOverforingField(key, cur[key])
                ).length + acc,
            0
        );
    return errors !== 0;
}

function isPep2SelectedNo(answers) {
    if (answers.PM_PEP_V2 !== undefined && answers.PM_PEP_V2.value.toString() === 'r-12-2') {
        return true;
    }
    return false;
}

function isValidPepRadio(definition, answers) {
    if (isPep2SelectedNo(answers)) {
        return true;
    }
    if (!answers.PM_PEP_HVEM || !answers.PM_PEP_RELASJON) {
        return false;
    }
    if (definition === 'PM_PEP_HVEM') {
        return answers.PM_PEP_HVEM !== undefined;
    }
    if (definition === 'PM_PEP_RELASJON') {
        return answers.PM_PEP_RELASJON !== undefined;
    }
    return false;
}

export function validateRadioType(question, answers) {
    if (question.definisjon === 'PM_PEP_HVEM' || question.definisjon === 'PM_PEP_RELASJON') {
        return isValidPepRadio(question.definisjon, answers);
    }

    const answer = answers[question.definisjon];
    if (questionTriggerCRS(question, answers)) {
        return !validateCRS(question, answers, true);
    }

    if (questionTriggerOverforing(question, answers)) {
        return !hasErrorInOverforing(question, answers);
    }

    if (question.definisjon === 'PM_PEP_V2' || question.type === 'RADIO_CLASSIC') {
        return true;
    }

    return validateThatAnswerHasTruthyValueAndNoError(answer);
}

function isValidPepPositionDescr(question, answers) {
    if (isPep2SelectedNo(answers)) {
        return true;
    }
    return (
        answers.PM_PEP_POSITION_DESCR !== undefined &&
        validerMonster(answers.PM_PEP_POSITION_DESCR.value, question.valideringsMonster)
    );
}

function validateTextType(question, answers) {
    if (question.definisjon === 'PM_PEP_POSITION_DESCR') {
        return isValidPepPositionDescr(question, answers);
    }

    if (isSubQuestionAndNotTriggerExistsInAnswers(question, answers)) {
        return true;
    }

    const answer = answers[question.definisjon];

    if (!validateSibling(question, answers) && (answer === undefined || answer.error)) {
        return false;
    }

    if (validateSibling(question, answers)) {
        return true;
    }

    return validateQuestionWithPossibleSubQuestion(question, answers);
}

function validateNumberType(question, answers) {
    if (isSubQuestionAndNotTriggerExistsInAnswers(question, answers)) {
        return true;
    }

    return validateQuestionWithPossibleSubQuestion(question, answers);
}

export function validateLandType(question, answers) {
    const answer = answers[question.definisjon];
    if (question.isSubQuestion) {
        return validateQuestionWithPossibleSubQuestion(question, answers);
    }
    return validateThatAnswerHasTruthyValueAndNoError(answer);
}

export function validateAktorType(question, answers, postalCodesList) {
    const answer = answers && answers[question.definisjon] && answers[question.definisjon].value;
    const keys = Object.keys(getAktorDetailsFields());
    const actor = answersToActor(answer);

    // Make sure that sum of errors in aktor entries equal 0
    return answer && keys.filter((key) => !isValidAktor(key, actor, question.config, postalCodesList)).length === 0;
}

export function validateFatcaCrsV2Country(value) {
    if (!value.country || value.country === '') {
        return false;
    }

    return true;
}

export function validateCrsV2TaxNumber(value) {
    if (!value.taxNumber && !value.taxNumberMissing) {
        return false;
    }

    return !(value.taxNumber && value.taxNumberMissing);
}

export function validateFatcaV2TaxNumber(value) {
    if (!value.taxNumber && !value.taxNumberMissing) {
        return false;
    }

    if (value.taxNumber && value.taxNumberMissing) {
        return false;
    }

    if (value.showTaxNumberMissing) {
        return value.taxNumberMissing?.length > 9;
    }

    const rawNumber = value.taxNumber.replace(/-/g, '');

    return rawNumber && rawNumber.match(/^[0-9]+$/) != null && rawNumber.length === 9;
}

export function validateFatcaCrsV2Confirmation(value) {
    return value.taxRelationEnded !== undefined || value.taxRelationValid !== undefined;
}

export function validateFatcaV2(value) {
    return validateFatcaCrsV2Country(value) && validateFatcaV2TaxNumber(value) && validateFatcaCrsV2Confirmation(value);
}

export function validateCrsV2(value) {
    return validateFatcaCrsV2Country(value) && validateCrsV2TaxNumber(value) && validateFatcaCrsV2Confirmation(value);
}

export function validateCrsV2Type(question, answers) {
    if (!answers) {
        return false;
    }

    const parentAnswer = answers.CRS_V2;
    // Type radio negative answer ends with '-2'
    if (parentAnswer && parentAnswer.value[0].endsWith('-2')) {
        return true;
    }

    if (!answers.CRS_V2_DETAILS) {
        return true;
    }

    if (!answers.CRS_V2_DETAILS.value || !answers.CRS_V2_DETAILS.value.length > 0) {
        return false;
    }

    return answers.CRS_V2_DETAILS.value.map((crs) => validateCrsV2(crs)).filter((res) => res === false).length === 0;
}

export function validateFatcaV2Type(question, answers) {
    if (!answers) {
        return false;
    }

    const parentAnswer = answers.FATCA_V2;
    // Type radio negative answer ends with '-2'
    if (parentAnswer && parentAnswer.value[0].endsWith('-2')) {
        return true;
    }

    if (!answers.FATCA_V2_DETAILS) {
        return true;
    }

    if (!answers.FATCA_V2_DETAILS.value || !answers.FATCA_V2_DETAILS.value.length > 0) {
        return false;
    }

    return (
        answers.FATCA_V2_DETAILS.value.map((fatca) => validateFatcaV2(fatca)).filter((res) => res === false).length ===
        0
    );
}

export function validateAktorListType(question, answers, postalCodesList) {
    const answer = answers && answers[question.definisjon];
    // Allow empty values in optional field
    if (!answer || (answer && !answer.value)) {
        return true;
    }
    const keys = Object.keys(getAktorDetailsFields());
    // Make sure that sum of errors in all entries equal 0, empty answer or value must return false
    const errors =
        answer &&
        answer.value &&
        answer.value.reduce((acc, subAnswer) => {
            const actor = answersToActor(subAnswer);
            return keys.filter((key) => !isValidAktor(key, actor, question.config, postalCodesList)).length + acc;
        }, 0);
    return errors === 0;
}

export function validateDisponentListType(question, answers, postalCodesList) {
    const answer = answers && answers[question.definisjon];
    // Allow empty values in optional field
    if (!answer || (answer && !answer.value)) {
        return true;
    }
    const keys = Object.keys(getDisponentDetaljerFields());
    // Make sure that sum of errors in all disponent entries equal 0, empty answer or value must return false
    const errors =
        answer &&
        answer.value &&
        answer.value.reduce((acc, subAnswer) => {
            const manager = answersToManager(subAnswer);
            const errorCount = keys.filter(
                (key) => !isValidDisponent(key, manager, question.config, postalCodesList)
            ).length;
            return errorCount + acc;
        }, 0);
    return errors === 0;
}

export function validateRRType(question, answers, postalCodesList) {
    const isNoRRChosen = answers[BM_AKTOR_RR] && answers[BM_AKTOR_RR].value[0] === BM_AKTOR_RR_NO_VALUE;
    if (isNoRRChosen) {
        return true;
    }
    if (isSubQuestionAndNotTriggerExistsInAnswers(question, answers)) {
        return true;
    }
    const answer = answers && answers[question.definisjon];
    const keys = Object.keys(getReelleRettighetshaverFields());
    // Make sure that sum of errors in all RR entries equal 0, empty answer or value must return false
    const errors =
        answer &&
        answer.value &&
        answer.value.reduce((acc, subAnswer) => {
            const beneficialOwner = answersToBeneficialOwner(subAnswer);
            return keys.filter((key) => !isValid(key, beneficialOwner, question.config, postalCodesList)).length + acc;
        }, 0);
    return errors === 0;
}

function isValidAnswer(question, answers, postalCodesList) {
    switch (question.type) {
        case SPORSMALSTYPE_CHECKBOX_CAP:
            return validateCheckboxType(question, answers);

        case SPORSMALSTYPE_RADIO_CAP:
        case SPORSMALSTYPE_RADIO_CLASSIC_CAP:
            return validateRadioType(question, answers);

        case SPORSMALSTYPE_TEXT_CAP:
        case SPORSMALSTYPE_TEXT_AREA_CAP:
            return validateTextType(question, answers);

        case SPORSMALSTYPE_NUMBER_CAP:
        case SPORSMALSTYPE_AMOUNT_CAP:
            return validateNumberType(question, answers);

        case SPORSMALSTYPE_SELECT_CAP:
        case SPORSMALSTYPE_LAND_CAP:
            return validateLandType(question, answers);

        case SPORSMALSTYPE_AKTOR_CAP:
            return validateAktorType(question, answers, postalCodesList);

        case SPORSMALSTYPE_AKTOR_LIST_CAP:
            return validateAktorListType(question, answers, postalCodesList);

        case SPORSMALSTYPE_DISPONENT_LIST_CAP:
            return validateDisponentListType(question, answers, postalCodesList);

        case SPORSMALSTYPE_RR_CAP:
            return validateRRType(question, answers, postalCodesList);
        case SPORSMALSTYPE_CRS_V2_CAP:
            return validateCrsV2Type(question, answers);
        case SPORSMALSTYPE_FATCA_V2_CAP:
            return validateFatcaV2Type(question, answers);
        default:
            return true;
    }
}

const hasAnswer = (ans) => ans != null && ans.value != null && Array.isArray(ans.value) && ans.value.length !== 0;

const getIsFrivilligAktorFieldWithValue = (question, answers) =>
    !isRequiredQuestion(question.utfyllingsregel) &&
    hasAnswer(answers[question.definisjon]) &&
    [SPORSMALSTYPE_AKTOR_LIST_CAP, SPORSMALSTYPE_DISPONENT_LIST_CAP].includes(question.type);

export function needToValidate(question) {
    if (question.isSubQuestion) {
        return !!isRequiredQuestion(question.utfyllingsregel);
    }

    if (question.synlighet === SPORSMAL_SYSTEM) {
        return false;
    }

    if (question.muligeSvar && question.muligeSvar.length > 0) {
        if (getPreselected(question) && isRequiredWhenSelected(question)) {
            return false;
        }
    }

    return isRequiredQuestion(question.utfyllingsregel);
}

function validateSharesAnswers(answers) {
    if (answers[BM_AKTOR_RR] && answers[BM_AKTOR_RR].value[0] === BM_AKTOR_RR_NO_VALUE) return true;
    const subQuestions = answers[BM_AKTOR_RR_DETALJER]?.value;
    return calculateTotalOwnershipShares(subQuestions) <= 100;
}

function validateQuestions(answers, questions, postalCodesList) {
    return questions.map((question) => {
        if (needToValidate(question) || getIsFrivilligAktorFieldWithValue(question, answers)) {
            const validAnswer = !!isValidAnswer(question, answers, postalCodesList);
            if (!validAnswer) {
                return false;
            }
        }
        return true;
    });
}

export function validateForm(answers, questions, postalCodesList) {
    if (!answers || !validateSharesAnswers(answers)) {
        return false;
    }
    return validateQuestions(answers, questions, postalCodesList).filter((valid) => valid === false).length === 0;
}
